import React, {useState, useRef} from 'react'
import {get} from 'lodash'
import {
  AlignItems,
  AppWrapper,
  Button,
  ButtonType,
  Columns,
  ComponentColor,
  ComponentSize,
  ComponentStatus,
  FlexBox,
  FlexDirection,
  Form,
  FunnelPage,
  Grid,
  Input,
  InputLabel,
  InputToggleType,
  Panel,
  Toggle,
} from '@influxdata/clockface'
import LogoWithCubo from '../LogoWithCubo'
import FunnelHeader from '../shared/FunnelHeader'
import FunnelSubHeader from '../shared/FunnelSubheader'
import './TailoredQuestionnaire.scss'
import {CodeIcon, DatabaseIcon, ToolsIcon} from './Icons'
import {rudderanalytics} from './eventing/rudderstack'

enum UseCases {
  BuildAnApp = 'build-an-application',
  MonitorAnalyze = 'monitor-analyze-data',
  LearnInfluxDB = 'learn-about-influxdb',
  Other = 'use-case-other',
}

enum KindOfDataPoured {
  DataFromSensors = 'data-from-sensors',
  MetricsDataFromApps = 'metrics-data-from-apps',
  TracingDataFromServices = 'tracing-from-services',
  IDontKnow = 'i-dont-know',
  Other = 'kind-data-other',
}

enum WorkloadMigrationType {
  NewWorkload = 'new-workload',
  MigrationFromOss = 'migration-from-oss',
  MigrationFromCloud = 'migration-from-cloud',
  Other = 'migration-other',
}

interface Props {
  email: string
}

type Response = string | null
export const TailoredQuestionnaire = ({email}: Props): JSX.Element => {
  // Use Case - State
  const [useCase, setUseCase] = useState<Response>(null)
  const [otherUseCase, setOtherUseCaseText] = useState<Response>('')

  // Kind of data poured - State
  const [kindOfDataSelections, setKindOfDataSelections] = useState<Response[]>(
    []
  )
  const [otherKindOfData, setOtherKindOfDataText] = useState<Response>('')

  // Workload Migration Type - State
  const [workloadMigrationType, setWorkloadMigrationType] =
    useState<Response>(null)
  const [otherWorkloadType, setOtherWorkloadTypeText] = useState<Response>('')

  // Ref for Form Element
  const formRef = useRef(null)

  // Use Case selection - State handlers
  const handleUseCaseSelection = (useCaseSelection: string) => {
    setUseCase(useCaseSelection)
  }
  const handleUseCaseOtherInput = (e) => {
    const userInput = e.currentTarget.value
    setOtherUseCaseText(userInput)
  }

  // Workload Migration selection - State handlers
  const handleWorkloadMigrationQuestion = (
    workloadMigrationSelection: string
  ) => {
    setWorkloadMigrationType(workloadMigrationSelection)
  }
  const handleWorkloadMigrationOtherInput = (e) => {
    const userInput = e.currentTarget.value
    setOtherWorkloadTypeText(userInput)
  }

  // Kind of data selection - State handlers
  const handleKindOfDataSelection = (kindSelection) => {
    // if array has it, remove
    // else add
    if (kindOfDataSelections.includes(kindSelection)) {
      setKindOfDataSelections(
        kindOfDataSelections.filter((item) => item !== kindSelection)
      )
    } else {
      setKindOfDataSelections([...kindOfDataSelections, kindSelection])
    }
  }
  const handleKindOfDataOtherInput = (e) => {
    const userInput = e.currentTarget.value
    setOtherKindOfDataText(userInput)
  }

  const handleRedirect = (redirectUrl) => {
    window.location.href = redirectUrl
  }

  const handleSubmit = () => {
    const responses = {
      email: email,
      useCase: useCase === UseCases.Other ? otherUseCase : useCase,
      kindOfDataPoured: otherKindOfData
        ? kindOfDataSelections.join(',').concat(`,${otherKindOfData}`)
        : kindOfDataSelections.join(','),
      workloadMigrationType:
        workloadMigrationType === WorkloadMigrationType.Other
          ? otherWorkloadType
          : workloadMigrationType,
    }
    rudderanalytics.track('In-app questionnaire responses', responses)
    handleRedirect(get(formRef.current, 'props.action', ''))
  }

  const validateData = () => {
    const useCasesValidation =
      (useCase && useCase !== UseCases.Other) ||
      (useCase === UseCases.Other && !!otherUseCase)
    const kindOfDataValidation =
      (kindOfDataSelections.length &&
        !kindOfDataSelections.includes(KindOfDataPoured.Other)) ||
      !!otherKindOfData
    const workloadMigrationValidation =
      (workloadMigrationType &&
        workloadMigrationType !== WorkloadMigrationType.Other) ||
      (workloadMigrationType === WorkloadMigrationType.Other &&
        !!otherWorkloadType)
    return (
      useCasesValidation && kindOfDataValidation && workloadMigrationValidation
    )
  }
  return (
    <AppWrapper>
      <FunnelPage logo={<LogoWithCubo />}>
        <Grid.Column
          widthSM={Columns.Ten}
          offsetSM={Columns.One}
          widthMD={Columns.Eight}
          offsetMD={Columns.Two}
        >
          <FunnelHeader text="How do you plan to use InfluxDB?" />
          <FunnelSubHeader text="We’ll use this to customize your experience" />
          <Form
            action="https://cloud2.influxdata.com"
            id="personalize-form"
            onSubmit={handleSubmit}
            ref={formRef}
          >
            <Panel>
              <Panel.Body>
                <FlexBox className="question-row" margin={ComponentSize.Large}>
                  <div className="question-icon">
                    <ToolsIcon />
                  </div>
                  <Form.Element
                    label="What are you looking to do on InfluxDB?"
                    className="question-text"
                    required={true}
                  >
                    <FlexBox
                      margin={ComponentSize.Medium}
                      direction={FlexDirection.Column}
                      alignItems={AlignItems.FlexStart}
                    >
                      <Toggle
                        tabIndex={1}
                        value={UseCases.BuildAnApp}
                        id={UseCases.BuildAnApp}
                        type={InputToggleType.Radio}
                        size={ComponentSize.ExtraSmall}
                        onChange={handleUseCaseSelection}
                        checked={useCase === UseCases.BuildAnApp}
                      >
                        <InputLabel htmlFor={UseCases.BuildAnApp}>
                          Build an Application
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={2}
                        value={UseCases.MonitorAnalyze}
                        id={UseCases.MonitorAnalyze}
                        type={InputToggleType.Radio}
                        size={ComponentSize.ExtraSmall}
                        onChange={handleUseCaseSelection}
                        checked={useCase === UseCases.MonitorAnalyze}
                      >
                        <InputLabel htmlFor={UseCases.MonitorAnalyze}>
                          Monitor & Analyze Data
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={3}
                        value={UseCases.LearnInfluxDB}
                        id={UseCases.LearnInfluxDB}
                        type={InputToggleType.Radio}
                        size={ComponentSize.ExtraSmall}
                        onChange={handleUseCaseSelection}
                        checked={useCase === UseCases.LearnInfluxDB}
                      >
                        <InputLabel htmlFor={UseCases.LearnInfluxDB}>
                          Learn about InfluxDB
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={4}
                        value={UseCases.Other}
                        id={UseCases.Other}
                        type={InputToggleType.Radio}
                        size={ComponentSize.ExtraSmall}
                        onChange={handleUseCaseSelection}
                        checked={useCase === UseCases.Other}
                        style={{marginTop: '18px'}}
                      >
                        <InputLabel htmlFor={UseCases.Other}>
                          <Input
                            value={otherUseCase}
                            placeholder="Other"
                            size={ComponentSize.ExtraSmall}
                            style={{marginLeft: '-2px', width: '300px'}}
                            onChange={handleUseCaseOtherInput}
                            onFocus={() => setUseCase(UseCases.Other)}
                          />
                        </InputLabel>
                      </Toggle>
                    </FlexBox>
                  </Form.Element>
                </FlexBox>
                <FlexBox className="question-row" margin={ComponentSize.Large}>
                  <div className="question-icon code-icon">
                    <CodeIcon />
                  </div>
                  <Form.Element
                    label="What kind of data do you plan to bring into InfluxDB?"
                    className="question-text"
                    required={true}
                  >
                    <Form.HelpText text="Check all that are applicable to you" />
                    <FlexBox
                      margin={ComponentSize.Medium}
                      direction={FlexDirection.Column}
                      alignItems={AlignItems.FlexStart}
                      stretchToFitWidth={true}
                    >
                      <Toggle
                        tabIndex={5}
                        value={KindOfDataPoured.DataFromSensors}
                        id={KindOfDataPoured.DataFromSensors}
                        type={InputToggleType.Checkbox}
                        size={ComponentSize.Small}
                        checked={kindOfDataSelections.includes(
                          KindOfDataPoured.DataFromSensors
                        )}
                        onChange={handleKindOfDataSelection}
                      >
                        <InputLabel htmlFor={KindOfDataPoured.DataFromSensors}>
                          Data from IOT sensors, devices etc.
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={6}
                        value={KindOfDataPoured.MetricsDataFromApps}
                        id={KindOfDataPoured.MetricsDataFromApps}
                        type={InputToggleType.Checkbox}
                        size={ComponentSize.Small}
                        checked={kindOfDataSelections.includes(
                          KindOfDataPoured.MetricsDataFromApps
                        )}
                        onChange={handleKindOfDataSelection}
                      >
                        <InputLabel
                          htmlFor={KindOfDataPoured.MetricsDataFromApps}
                        >
                          Metrics data from server, network, infrastructure,
                          applications etc.
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={7}
                        value={KindOfDataPoured.TracingDataFromServices}
                        id={KindOfDataPoured.TracingDataFromServices}
                        type={InputToggleType.Checkbox}
                        size={ComponentSize.ExtraSmall}
                        checked={kindOfDataSelections.includes(
                          KindOfDataPoured.TracingDataFromServices
                        )}
                        onChange={handleKindOfDataSelection}
                      >
                        <InputLabel
                          htmlFor={KindOfDataPoured.TracingDataFromServices}
                        >
                          Tracing, events, or log data from applications
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={8}
                        value={KindOfDataPoured.IDontKnow}
                        id={KindOfDataPoured.IDontKnow}
                        type={InputToggleType.Checkbox}
                        size={ComponentSize.ExtraSmall}
                        checked={kindOfDataSelections.includes(
                          KindOfDataPoured.IDontKnow
                        )}
                        onChange={handleKindOfDataSelection}
                      >
                        <InputLabel htmlFor={KindOfDataPoured.IDontKnow}>
                          I don’t know
                        </InputLabel>
                      </Toggle>
                      <div className="other-kind-of-data--input">
                        <Toggle
                          tabIndex={9}
                          value={KindOfDataPoured.Other}
                          id={KindOfDataPoured.Other}
                          type={InputToggleType.Checkbox}
                          size={ComponentSize.ExtraSmall}
                          onChange={handleKindOfDataSelection}
                          checked={kindOfDataSelections.includes(
                            KindOfDataPoured.Other
                          )}
                        ></Toggle>
                        <Input
                          placeholder="Other"
                          className="kind-input-field"
                          value={otherKindOfData}
                          size={ComponentSize.ExtraSmall}
                          onChange={handleKindOfDataOtherInput}
                        />
                      </div>
                    </FlexBox>
                  </Form.Element>
                </FlexBox>
                <FlexBox className="question-row" margin={ComponentSize.Large}>
                  <div className="question-icon">
                    <DatabaseIcon />
                  </div>
                  <Form.Element
                    label="Are you migrating an existing workload?"
                    required={true}
                  >
                    <FlexBox
                      className="answer-choices"
                      direction={FlexDirection.Column}
                      alignItems={AlignItems.FlexStart}
                      margin={ComponentSize.Medium}
                    >
                      <Toggle
                        tabIndex={10}
                        value={WorkloadMigrationType.NewWorkload}
                        id={WorkloadMigrationType.NewWorkload}
                        type={InputToggleType.Radio}
                        size={ComponentSize.ExtraSmall}
                        checked={
                          workloadMigrationType ===
                          WorkloadMigrationType.NewWorkload
                        }
                        onChange={handleWorkloadMigrationQuestion}
                      >
                        <InputLabel htmlFor={WorkloadMigrationType.NewWorkload}>
                          I'm starting a new workload.
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={11}
                        value={WorkloadMigrationType.MigrationFromOss}
                        id={WorkloadMigrationType.MigrationFromOss}
                        type={InputToggleType.Radio}
                        size={ComponentSize.ExtraSmall}
                        checked={
                          workloadMigrationType ===
                          WorkloadMigrationType.MigrationFromOss
                        }
                        onChange={handleWorkloadMigrationQuestion}
                      >
                        <InputLabel
                          htmlFor={WorkloadMigrationType.MigrationFromOss}
                        >
                          I'm migrating from InfluxDB OSS.
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={11}
                        value={WorkloadMigrationType.MigrationFromCloud}
                        id={WorkloadMigrationType.MigrationFromCloud}
                        type={InputToggleType.Radio}
                        size={ComponentSize.ExtraSmall}
                        checked={
                          workloadMigrationType ===
                          WorkloadMigrationType.MigrationFromCloud
                        }
                        onChange={handleWorkloadMigrationQuestion}
                      >
                        <InputLabel
                          htmlFor={WorkloadMigrationType.MigrationFromCloud}
                        >
                          I'm migrating from a previous InfluxDB Cloud.
                        </InputLabel>
                      </Toggle>
                      <Toggle
                        tabIndex={14}
                        value={WorkloadMigrationType.Other}
                        id={WorkloadMigrationType.Other}
                        type={InputToggleType.Radio}
                        checked={
                          workloadMigrationType === WorkloadMigrationType.Other
                        }
                        size={ComponentSize.ExtraSmall}
                        onChange={handleWorkloadMigrationQuestion}
                        style={{marginTop: '18px'}}
                      >
                        <InputLabel htmlFor={WorkloadMigrationType.Other}>
                          <Input
                            placeholder="I'm migrating from elsewhere (specify)."
                            size={ComponentSize.ExtraSmall}
                            style={{marginLeft: '-2px', width: '300px'}}
                            onChange={handleWorkloadMigrationOtherInput}
                            onFocus={() => {
                              setWorkloadMigrationType(
                                WorkloadMigrationType.Other
                              )
                            }}
                            value={otherWorkloadType}
                          />
                        </InputLabel>
                      </Toggle>
                    </FlexBox>
                  </Form.Element>
                </FlexBox>
              </Panel.Body>
            </Panel>
            <Button
              text="Continue"
              className="submit-button"
              size={ComponentSize.Large}
              color={ComponentColor.Primary}
              type={ButtonType.Submit}
              status={
                validateData()
                  ? ComponentStatus.Default
                  : ComponentStatus.Disabled
              }
            />
          </Form>
        </Grid.Column>
      </FunnelPage>
    </AppWrapper>
  )
}
